// GPU lightstyles: lightstyle animation is performed in the Brush fragment shader by blending
// up to 4 raw per-style lightmap layers (written once at map load). Dynamic lights use a
// separate per-page overlay texture updated per-frame by buildDlightOverlayChains/uploadLightmaps.
import {loadLightmapTextureSlot, loadLightmapOverlayTexture, createBlackTexture, bind, state as txState} from './texture'
import * as cl from './cl'
import * as vec from './vec'
import * as def from './def'
import * as GL from './GL'
import * as texture from './texture'
import * as con from './console'
import {cvr} from './r'
import { Face, Model, TexChain } from './types/Model'
import { V3 } from './types/Vector'

export const LM_BLOCK_WIDTH = 256
export const LM_BLOCK_HEIGHT = 256
export const MAXLIGHTMAPS = 512
export const MAX_LIGHTSTYLES = 64

// const cvr = {
// 	gl_overbright: {value: 1},
// 	gl_fullbrights: {value: 0},
// 	r_novis: {value: 0},
//   dynamic: {value:0},
// 	oldskyleaf: {value: 0}
// }

type LightmapPageEntry = {
	slots: (Uint8Array | null)[],
	overlay: Uint8Array | null
}

export type LightmapState = {
	lightmap_modified: boolean[],
	lightmap_rectchange: {l: number, t: number, w: number, h: number}[],
	lightstylevalue: Uint32Array,
	lightstyle_uniform: Float32Array,
	lightstyle_uniform_dirty: boolean,
	blocklights: Uint32Array,
	allocated: number[][],
	dlightframecount: number,
	last_lightmap_allocated: number,
	lightmap_pages: LightmapPageEntry[]
}

export const state: LightmapState = {
    lightmap_modified: [],
    lightmap_rectchange: [],
    lightstylevalue: new Uint32Array(new ArrayBuffer(256 * 4)),
    lightstyle_uniform: new Float32Array(65), // index 64 is always 0 (unused slot)
    lightstyle_uniform_dirty: true,
    blocklights: new Uint32Array(new ArrayBuffer(3 * LM_BLOCK_HEIGHT * LM_BLOCK_WIDTH)),
  allocated: Array.apply(null, new Array(MAXLIGHTMAPS)).map((): number[] => []),
  dlightframecount: 0,
  last_lightmap_allocated: 0,
  lightmap_pages: []
}

export const init = () => {	
	for (var i=0 ; i<256 ; i++)
		state.lightstylevalue[i] = 264;	

	state.lightmap_modified = Array.apply(null, new Array(MAXLIGHTMAPS)).map(() => false)
	state.lightmap_rectchange = Array.apply(null, new Array(MAXLIGHTMAPS)).map(() => ({l:0, t:0, w:0, h:0}))

	state.allocated = Array.apply(null, new Array(MAXLIGHTMAPS)).map(() => 
		Array.apply(null, new Array(LM_BLOCK_WIDTH)).map(() => 0))
	state.last_lightmap_allocated = 0;

	state.dlightframecount = 0; // no dlightcache
	state.lightmap_pages = []
	state.lightstyle_uniform_dirty = true
}

/*
========================
AllocBlock -- returns a texture number and the position inside it
========================
*/
const allocBlock = (surf: Face) => {
	var	i, j;
	var	best, best2;
	var	texnum;
	var w = (surf.extents[0]>>4)+1;
	var h = (surf.extents[1]>>4)+1;

	// ericw -- rather than searching starting at lightmap 0 every time,
	// start at the last lightmap we allocated a surface in.
	// This makes AllocBlock much faster on large levels (can shave off 3+ seconds
	// of load time on a level with 180 lightmaps), at a cost of not quite packing
	// lightmaps as tightly vs. not doing this (uses ~5% more lightmaps)
	for (texnum=state.last_lightmap_allocated ; texnum<MAXLIGHTMAPS ; texnum++, state.last_lightmap_allocated++)
	{
		best = LM_BLOCK_HEIGHT;

		for (i=0 ; i<LM_BLOCK_WIDTH-w ; i++)
		{
			best2 = 0;

			for (j=0 ; j<w ; j++)
			{
				if (state.allocated[texnum][i+j] >= best)
					break;
				if (state.allocated[texnum][i+j] > best2)
					best2 = state.allocated[texnum][i+j];
			}
			if (j == w)
			{	// this is a valid spot
				surf.light_s = i;
				surf.light_t = best = best2;
			}
		}

		if (best + h > LM_BLOCK_HEIGHT)
			continue;

		for (i=0 ; i<w ; i++)
			state.allocated[texnum][surf.light_s + i] = best + h;

		return texnum;
	}

	throw new Error ("AllocBlock: full");
}

export const createSurfaceLightmap = (model: Model, surf: Face) => {
	surf.lightmaptexturenum = allocBlock (surf);
	writeStyleLayers (model, surf);
}

export const addDynamicLights = (blocklights: Uint32Array, model: Model, surf: Face) => {
	//johnfitz

	var smax = (surf.extents[0] >> 4) + 1;
	var tmax = (surf.extents[1] >> 4) + 1;
	var tex = model.texinfo[surf.texinfo];
	var impact: V3 = vec.emptyV3(), local = []
	var sd, td, brightness, blidx = 0;

	for (var i = 0; i < cl.state.dlights.length; i++)
	{
		if (! (surf.dlightbits[i >> 5] & (1 << (i & 31))))
			continue;		// not lit by this light

		var rad = cl.state.dlights[i].radius;
		var dist = vec.dotProductV3(cl.state.dlights[i].origin, surf.plane.normal) -
				surf.plane.dist;

		rad -= Math.abs(dist);

		var minlight = cl.state.dlights[i].minlight;
		if (rad < minlight)
			continue;

		minlight = rad - minlight;

		for (var j=0 ; j<3 ; j++)
		{
			impact[j] = cl.state.dlights[i].origin[j] -
					surf.plane.normal[j] * dist;
		}

		local[0] = vec.dotProductV3(impact, tex.vecs[0]) + tex.vecs[0][3];
		local[1] = vec.dotProductV3(impact, tex.vecs[1]) + tex.vecs[1][3];

		local[0] -= surf.texturemins[0];
		local[1] -= surf.texturemins[1];

		//johnfitz -- lit support via lordhavoc
		var bl = blocklights;
		var cred = 256 // cl.state.dlights[i].color[0] * 256.0;
		var cgreen = 256 // cl.state.dlights[i].color[1] * 256.0;
		var cblue = 256 // cl.state.dlights[i].color[2] * 256.0;

		//johnfitz
		for (var t = 0; t < tmax; t++)
		{
			td = local[1] - (t << 4);
			if (td < 0)
				td = -td;
			td = Math.floor(td)

			for (var s = 0 ; s < smax ; s++)
			{
				sd = local[0] - (s << 4);
				if (sd < 0)
					sd = -sd;
				sd = Math.floor(sd)
				if (sd > td)
					dist = sd + (td>>1);
				else
					dist = td + (sd>>1);
				if (dist < minlight)
				//johnfitz -- lit support via lordhavoc
				{
					brightness = rad - dist;
					bl[blidx++] += Math.floor(brightness * cred);
					bl[blidx++] += Math.floor(brightness * cgreen);
					bl[blidx++] += Math.floor(brightness * cblue);
				} else {
					blidx += 3
				}
				//johnfitz
			}
		}
	}
}
// Write the raw (unscaled) per-style lightmap samples into per-page/per-slot staging buffers.
// Called at map load for every lightmapped surface; staging is later uploaded as GL textures.
const writeStyleLayers = (model: Model, surf: Face) => {
	const page = surf.lightmaptexturenum
	const smax = (surf.extents[0] >> 4) + 1
	const tmax = (surf.extents[1] >> 4) + 1
	const size = smax * tmax

	if (!state.lightmap_pages[page])
		state.lightmap_pages[page] = { slots: [], overlay: null }

	const pageEntry = state.lightmap_pages[page]

	if (!model || !model.lightdata) {
		// Full bright: write 255s into slot 0 so the GPU path can light this surface.
		// Unlit BSPs carry all-255 style bytes (styles = []), which the vertex buffer maps
		// to the zero-weight slot 64 — force style 0 so the slot-0 data gets a live weight.
		surf.styles[0] = 0
		if (!pageEntry.slots[0])
			pageEntry.slots[0] = new Uint8Array(LM_BLOCK_WIDTH * LM_BLOCK_HEIGHT * 4)
		const staging = pageEntry.slots[0]
		for (var t = 0; t < tmax; t++) {
			for (var s = 0; s < smax; s++) {
				const dstIdx = ((surf.light_t + t) * LM_BLOCK_WIDTH + (surf.light_s + s)) * 4
				staging[dstIdx] = 255
				staging[dstIdx + 1] = 255
				staging[dstIdx + 2] = 255
				staging[dstIdx + 3] = 255
			}
		}
		return
	}

	if (surf.lightofs < 0)
		return // No lightmap data for this surface — leave staging black

	for (var m = 0; m < surf.styles.length; m++) {
		if (!pageEntry.slots[m])
			pageEntry.slots[m] = new Uint8Array(LM_BLOCK_WIDTH * LM_BLOCK_HEIGHT * 4)
		const staging = pageEntry.slots[m]
		const srcBase = surf.lightofs + m * size * 3

		for (var t = 0; t < tmax; t++) {
			for (var s = 0; s < smax; s++) {
				const srcIdx = srcBase + (t * smax + s) * 3
				const dstIdx = ((surf.light_t + t) * LM_BLOCK_WIDTH + (surf.light_s + s)) * 4
				staging[dstIdx] = model.lightdata[srcIdx]
				staging[dstIdx + 1] = model.lightdata[srcIdx + 1]
				staging[dstIdx + 2] = model.lightdata[srcIdx + 2]
				staging[dstIdx + 3] = 255
			}
		}
	}
}

// GPU lightstyles: write raw per-style layers into per-page/per-slot staging at load time,
// then create GL textures for each slot. Dynamic lights are handled via per-page overlay textures
// written per-frame in buildDlightOverlayChains / uploadLightmaps.
export const buildLightmaps = (gl: WebGLRenderingContext, model: Model) => {

	for (var i=0 ; i<model.numfaces ; i++)
	{
		if (model.faces[i].flags & def.SURF.drawtiled)
			continue;
		createSurfaceLightmap (model, model.faces[i]);
	}

	// Create per-slot GL textures and reset dirty rects for every used page
	for (i = 0; i<MAXLIGHTMAPS; i++)
	{
		if (!state.allocated[i][0])
			break;		// no more used
		state.lightmap_modified[i] = false;
		state.lightmap_rectchange[i].l = LM_BLOCK_WIDTH;
		state.lightmap_rectchange[i].t = LM_BLOCK_HEIGHT;
		state.lightmap_rectchange[i].w = 0;
		state.lightmap_rectchange[i].h = 0;

		const pageEntry = state.lightmap_pages[i]
		if (pageEntry) {
			for (var slot = 0; slot < pageEntry.slots.length; slot++) {
				const slotData = pageEntry.slots[slot]
				if (slotData) {
					const slotName = `lightmap#${i}_s${slot}`
					loadLightmapTextureSlot(gl, i, slot, slotName, LM_BLOCK_WIDTH, LM_BLOCK_HEIGHT, slotData)
				}
			}
		}
	}

	// Create the shared black fallback texture (used for missing slots/overlay in later phases)
	createBlackTexture(gl)

	//johnfitz -- warn about exceeding old limits
	if (i >= 64)
		con.dPrint(`${i} lightmaps exceeds standard limit of 64 (max = ${MAXLIGHTMAPS}).\n`);
	//johnfitz
}


// Free the per-style staging buffers once every model's lightmaps are uploaded.
// Must not run between per-model buildLightmaps calls: models can share a page,
// and a fresh zeroed staging buffer would wipe earlier surfaces on re-upload.
// Overlay staging stays resident for the per-frame dlight path.
export const freeStagingSlots = () => {
	for (const pageEntry of state.lightmap_pages) {
		if (pageEntry)
			pageEntry.slots = []
	}
}

// Grow the lightmap_rectchange dirty rect for page to include surf's rect.
const growLightmapRect = (page: number, surf: Face) => {
	state.lightmap_modified[page] = true
	const theRect = state.lightmap_rectchange[page]
	if (surf.light_t < theRect.t) {
		if (theRect.h)
			theRect.h += theRect.t - surf.light_t
		theRect.t = surf.light_t
	}
	if (surf.light_s < theRect.l) {
		if (theRect.w)
			theRect.w += theRect.l - surf.light_s
		theRect.l = surf.light_s
	}
	const smax = (surf.extents[0] >> 4) + 1
	const tmax = (surf.extents[1] >> 4) + 1
	if ((theRect.w + theRect.l) < (surf.light_s + smax))
		theRect.w = (surf.light_s - theRect.l) + smax
	if ((theRect.h + theRect.t) < (surf.light_t + tmax))
		theRect.h = (surf.light_t - theRect.t) + tmax
}

// Write dlight contribution for surf into the page's overlay staging, upload lazily.
const buildDlightOverlay = (model: Model, surf: Face) => {
	const page = surf.lightmaptexturenum
	const smax = (surf.extents[0] >> 4) + 1
	const tmax = (surf.extents[1] >> 4) + 1
	const size = smax * tmax

	if (!state.lightmap_pages[page])
		state.lightmap_pages[page] = { slots: [], overlay: null }
	const pageEntry = state.lightmap_pages[page]

	if (!pageEntry.overlay) {
		pageEntry.overlay = new Uint8Array(LM_BLOCK_WIDTH * LM_BLOCK_HEIGHT * 4)
		loadLightmapOverlayTexture(GL.getContext(), page,
			`lightmap#${page}_overlay`, LM_BLOCK_WIDTH, LM_BLOCK_HEIGHT, pageEntry.overlay)
	}

	// Compute dlight contribution into blocklights
	state.blocklights.fill(0, 0, size * 3)
	addDynamicLights(state.blocklights, model, surf)

	// Pack >> 7, clamp 255 into overlay staging at the surface rect
	const overlay = pageEntry.overlay
	var blockidx = 0
	for (var t = 0; t < tmax; t++) {
		for (var s = 0; s < smax; s++) {
			const dstIdx = ((surf.light_t + t) * LM_BLOCK_WIDTH + (surf.light_s + s)) * 4
			overlay[dstIdx] = Math.min(state.blocklights[blockidx++] >> 7, 255)
			overlay[dstIdx + 1] = Math.min(state.blocklights[blockidx++] >> 7, 255)
			overlay[dstIdx + 2] = Math.min(state.blocklights[blockidx++] >> 7, 255)
			overlay[dstIdx + 3] = 255
		}
	}

	growLightmapRect(page, surf)
}

// Zero the overlay rect for a previously dlit surface (called when dlight has gone away).
const clearOverlayRect = (surf: Face) => {
	const page = surf.lightmaptexturenum
	const pageEntry = state.lightmap_pages[page]
	if (!pageEntry || !pageEntry.overlay)
		return
	const smax = (surf.extents[0] >> 4) + 1
	const tmax = (surf.extents[1] >> 4) + 1
	const overlay = pageEntry.overlay
	for (var t = 0; t < tmax; t++) {
		for (var s = 0; s < smax; s++) {
			const dstIdx = ((surf.light_t + t) * LM_BLOCK_WIDTH + (surf.light_s + s)) * 4
			overlay[dstIdx] = 0
			overlay[dstIdx + 1] = 0
			overlay[dstIdx + 2] = 0
			overlay[dstIdx + 3] = 255
		}
	}
	growLightmapRect(page, surf)
}

// Per-frame dlight overlay chain walk (cheap: only two compares per surface).
export const buildDlightOverlayChains = (model: Model, chain: TexChain) => {
	// r_dynamic 0 skips overlay builds; cached rects still fall through to the
	// clear branch below so already-lit surfaces fade out instead of freezing.
	const dynamic = cvr.dynamic.value !== 0
	for (var i = 0; i < model.textures.length; i++) {
		const t = model.textures[i]
		if (!t || !t.texturechains[chain])
			continue
		for (var s = t.texturechains[chain]; s; s = s.texturechain) {
			if (s.culled || (s.flags & def.SURF.drawtiled))
				continue
			if (dynamic && s.dlightframe === state.dlightframecount) {
				buildDlightOverlay(model, s)
				s.cached_dlight = true
			} else if (s.cached_dlight) {
				clearOverlayRect(s)
				s.cached_dlight = false
			}
		}
	}
}

// Upload dirty rects of dlight overlay textures.
export const uploadLightmaps = (gl: WebGLRenderingContext) => {
	for (var i = 0; i < MAXLIGHTMAPS; i++) {
		if (!state.lightmap_modified[i])
			continue

		state.lightmap_modified[i] = false
		const theRect = state.lightmap_rectchange[i]

		const pageEntry = state.lightmap_pages[i]
		if (!pageEntry || !pageEntry.overlay)
			continue

		const overlayTex = txState.lightmap_overlay_textures[i]
		if (!overlayTex)
			continue

		texture.bind(0, overlayTex.texnum)
		const data = pageEntry.overlay.subarray(theRect.t * LM_BLOCK_WIDTH * 4)
		gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, theRect.t, LM_BLOCK_WIDTH, theRect.h, gl.RGBA, gl.UNSIGNED_BYTE, data)

		theRect.l = LM_BLOCK_WIDTH
		theRect.t = LM_BLOCK_HEIGHT
		theRect.h = 0
		theRect.w = 0
	}
}


